import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { ApiError, errorMessage, fieldErrors, reportError } from "../../shared/api/errors.ts";
import { useAppEnvironment } from "../../shared/environment.tsx";
import { useReturnTo, withReturnTo } from "../../shared/session/returnTo.ts";
import { useSession } from "../../shared/session/SessionProvider.tsx";
import type { TelegramLogin } from "../../shared/telegram/telegramLogin.ts";
import { Button } from "../../shared/ui/Button/Button.tsx";
import { Input } from "../../shared/ui/Input/Input.tsx";
import { AuthScreen } from "../auth/AuthScreen.tsx";
import { InstallApp } from "../../shared/install/InstallApp.tsx";
import styles from "./LoginPage.module.css";

const FIELDS = ["email", "password"] as const;
type Field = (typeof FIELDS)[number];
type Errors = Partial<Record<Field | "form", string>>;

/** 02.01–02.04 / AUTHORIZATION. После входа GuestOnly уводит на исходный URL или на главную. */
export function LoginPage() {
  const { login } = useSession();
  const { telegramLogin } = useAppEnvironment();
  const registerLink = withReturnTo("/register", useReturnTo());
  const [values, setValues] = useState<Record<Field, string>>({ email: "", password: "" });
  const [errors, setErrors] = useState<Errors>({});
  const mutation = useMutation({ mutationFn: login, onError: (error) => setErrors(errorsFrom(error)) });

  function change(field: Field, value: string) {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      const next = { ...current };
      delete next[field];
      delete next.form;
      return next;
    });
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const email = values.email.trim();
    const local: Errors = {};
    if (!email) local.email = "Введите email";
    if (!values.password) local.password = "Введите пароль";
    setErrors(local);
    if (Object.keys(local).length === 0) mutation.mutate({ email, password: values.password });
  }

  return (
    <AuthScreen
      title="Авторизация"
      subtitle="Войдите, чтобы продолжить"
      footer={{ prompt: "Нет аккаунта?", label: "Зарегистрироваться", to: registerLink }}
    >
      <div className={styles.methods}>
        <form className={styles.form} onSubmit={submit} noValidate>
          <Input
            label="Email"
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            placeholder="user@mail.ru"
            value={values.email}
            onChange={(event) => change("email", event.target.value)}
            error={errors.email}
          />
          {/* «Забыли пароль?» из макета не выводится: сброс пароля вне MVP. */}
          <Input
            label="Пароль"
            type="password"
            name="password"
            autoComplete="current-password"
            placeholder="•••••••"
            value={values.password}
            onChange={(event) => change("password", event.target.value)}
            error={errors.password}
          />
          {errors.form && (
            <p className={styles.formError} role="alert">
              {errors.form}
            </p>
          )}
          <Button type="submit" loading={mutation.isPending}>
            Войти
          </Button>
        </form>
        {telegramLogin && <TelegramSignIn telegramLogin={telegramLogin} />}
        <InstallApp />
      </div>
    </AuthScreen>
  );
}

/**
 * «Войти через Telegram». В Figma кнопки нет, поэтому она повторяет пару «Primary — или — Destructive»
 * из 09.01 / DEVICE / Empty (Figma 339:2180). После входа GuestOnly уводит туда же, куда и вход по email.
 */
function TelegramSignIn({ telegramLogin }: { telegramLogin: TelegramLogin }) {
  const { loginWithTelegram } = useSession();
  const [error, setError] = useState<string | null>(null);
  // Повторное нажатие может открыть окно ещё раз: войти нужно один раз, даже если ответят оба.
  const signingIn = useRef(false);
  const mutation = useMutation({
    mutationFn: loginWithTelegram,
    onError: (cause) => setError(errorMessage(cause)),
    onSettled: () => {
      signingIn.current = false;
    },
  });

  useEffect(() => telegramLogin.prepare(), [telegramLogin]);

  // Пока открыто окно Telegram, кнопка не блокируется: если браузер не дал открыть окно,
  // виджет не вызовет callback, и иначе кнопка зависла бы в загрузке.
  async function start() {
    setError(null);
    let data;
    try {
      data = await telegramLogin.authorize();
    } catch (cause) {
      // Не загрузился виджет с telegram.org: наш backend тут ни при чём.
      reportError(cause);
      setError("Telegram сейчас недоступен. Попробуйте позже");
      return;
    }
    if (!data || signingIn.current) return;
    signingIn.current = true;
    mutation.mutate(data);
  }

  return (
    <>
      <p className={styles.or}>или</p>
      <Button variant="destructive" loading={mutation.isPending} onClick={start}>
        Войти через Telegram
      </Button>
      {error && (
        <p className={styles.formError} role="alert">
          {error}
        </p>
      )}
    </>
  );
}

function errorsFrom(error: Error): Errors {
  // В макете 02.03 «Неверный email или пароль» стоит под полем Email.
  if (error instanceof ApiError && error.code === "invalid_credentials") return { email: errorMessage(error) };
  const byField = fieldErrors(error, FIELDS);
  return Object.keys(byField).length > 0 ? byField : { form: errorMessage(error) };
}
